import { onRemoteScreenLive } from '../services/screenShareService';
import { vrLift } from './VR/seatedMode';
import { mergeAvatarAppearance } from '../avatar/appearance';
import { sampleAvatarHeight } from '../avatar/calibration';
import { AvatarTracking, decodeTracking, relativeTrackingPose } from '../avatar/tracking';
import React, { useEffect, useRef, useState, Suspense, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useThrottledFrame } from '../hooks/useThrottledFrame';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { getChannel, getPresenceKey, disconnectChannel, onChannelSubscribed } from '../services/realtimeManager';
import { supabase } from '../config/supabase';
import { withRemoteOrigin } from '../services/worldSync';
import { useWorldStore, DEFAULT_AVATAR_URL } from '../stores/worldStore';
import FixedRetargetedAvatar from './FixedRetargetedAvatar';
import GhostAvatar from './GhostAvatar';
import { FallbackOnError } from './three/FallbackOnError';
// Safety (mute / block / report): hides blocked people, makes avatars clickable.
import PlayerSafetyGate from './safety/PlayerSafetyGate';
import SafetyWorldHost from './safety/SafetyWorldHost';
import { toLiteUrls } from '../stores/liteStore';

// ---- Compact move-packet helpers ----
const ANIM_TO_CODE: Record<string, string> = { idle: 'i', walk: 'w', run: 'r' };
const CODE_TO_ANIM: Record<string, 'idle' | 'walk' | 'run'> = { i: 'idle', w: 'walk', r: 'run' };

function encodeMove(key: string, position: [number, number, number], rotation: number, animationState: 'idle' | 'walk' | 'run') {
  return {
    k: key,
    p: [
      Math.round(position[0] * 100) / 100,
      Math.round(position[1] * 100) / 100,
      Math.round(position[2] * 100) / 100,
    ] as [number, number, number],
    r: Math.round(rotation * 100) / 100,
    a: ANIM_TO_CODE[animationState] || 'i',
  };
}

function decodeMove(data: any): { key: string; position: [number, number, number]; rotation: number; animationState: 'idle' | 'walk' | 'run'; tracking: AvatarTracking | null; grounded?: boolean } | null {
  const validRoot = (p: any, r: any) => Array.isArray(p) && p.length === 3 && p.every((v: any) => typeof v === 'number' && Number.isFinite(v)) && typeof r === 'number' && Number.isFinite(r);
  if (typeof data?.k === 'string' && validRoot(data.p, data.r)) {
    return { key: data.k, position: data.p, rotation: data.r, animationState: CODE_TO_ANIM[data.a] || 'idle', tracking: decodeTracking(data.tracking), grounded: typeof data.grounded === 'boolean' ? data.grounded : undefined };
  }
  if (typeof data?.key === 'string' && validRoot(data.position, data.rotation)) {
    return { key: data.key, position: data.position, rotation: data.rotation, animationState: data.animationState || 'idle', tracking: null };
  }
  return null;
}

// Read the local user's Meshy animation URLs (same source FixedRetargetedAvatar
// uses for the local avatar). We broadcast these so remote peers can animate our
// avatar with OUR clips — clips are rig-specific, so a peer's shared Mixamo
// fallback won't bind to our Meshy skeleton and leaves us in a T-pose.
function readLocalAnimUrls(): Record<string, string> | null {
  try {
    const stored = localStorage.getItem('eb-avatar-animations');
    const local = stored ? (JSON.parse(stored) as Record<string, string>) : {};
    const fromStore = useWorldStore.getState().avatarAnimations || {};
    const merged = { ...local, ...fromStore };
    return Object.keys(merged).length > 0 ? merged : null;
  } catch {
    return null;
  }
}

// ---- Types ----

interface RemotePlayerInfo {
  id: string;
  avatarUrl: string;
  lookTextureUrl?: string | null;
  userName: string;
  animUrls?: Record<string, string> | null;
}

interface RemotePlayerTransform {
  grounded?: boolean;
  tracking?: AvatarTracking | null;
  trackingReceivedAt?: number;
  position: [number, number, number];
  rotation: number;
  animationState: 'idle' | 'walk' | 'run';
  // Latest emote to play on this remote avatar (nonce lets the same emote
  // re-trigger). Consumed by FixedRetargetedAvatar's remote playback path.
  emote?: { name: string; nonce: number } | null;
  // Render LOD tier, assigned centrally below by camera distance + rank.
  lod?: 'full' | 'frozen' | 'billboard';
}

// ---- Avatar LOD tuning ----
// Only the nearest LOD_MAX_FULL players (within LOD_FULL_DIST metres) get full
// animation + arm correction; farther/lower-rank players freeze their pose,
// and beyond LOD_BILLBOARD_DIST they render as a cheap capsule. This caps the
// per-frame skinning/animation cost regardless of how many players are present
// — the real bottleneck for 20+ rooms, especially on Quest.
const LOD_MAX_FULL = 8;
const LOD_FULL_DIST = 30;
const LOD_BILLBOARD_DIST = 45;
const LOD_INTERVAL_MS = 250;
const _lodVec = new THREE.Vector3();

// Eye height of the desktop view-only camera (VRViewer FP_EYE_HEIGHT).
const VIEW_ONLY_EYE_HEIGHT = 1.6;

// Timeout (ms) after which a player is considered disconnected if no heartbeat
const HEARTBEAT_TIMEOUT = 8000;
const HEARTBEAT_INTERVAL = 3000;

// ---- Remote avatar ----

// Peers without an avatar (view-only visitors) appear as a friendly ghost
// carrying their name; a peer whose avatar model is still loading shows the same
// ghost, fainter, until the model pops in. See GhostAvatar.tsx.

const RemoteAvatar: React.FC<{
  info: RemotePlayerInfo;
  transformRef: React.MutableRefObject<RemotePlayerTransform>;
  userId?: string | null;
}> = React.memo(({ info, transformRef, userId = null }) => {
  const avatarUrl = info.avatarUrl;
  if (!avatarUrl || avatarUrl === 'skip') {
    return <GhostAvatar transformRef={transformRef} name={info.userName} />;
  }
  // A peer's model that fails to load (dead link, bad file) shows them as the
  // ghost instead of taking the whole scene down for everyone else.
  return (
    <FallbackOnError key={avatarUrl} what={`avatar for ${info.userName}`} fallback={<GhostAvatar transformRef={transformRef} name={info.userName} />}>
    <Suspense fallback={<GhostAvatar transformRef={transformRef} name={info.userName} loading />}>
      {/* Keyed by model: a mid-session avatar switch remounts cleanly (the
          ghost stands in while the new model loads) instead of re-rigging
          the old body in place. */}
      <FixedRetargetedAvatar
        key={avatarUrl}
        avatarUrl={avatarUrl}
        lookTextureUrl={info.lookTextureUrl || null}
        position={[0, 0, 0]}
        isRemote
        remoteAnimUrls={info.animUrls || null}
        transformRef={transformRef}
        voiceUserId={userId}
        userName={info.userName}
        showNameTag
        disableCameraControl
      />
    </Suspense>
    </FallbackOnError>
  );
});

// ---- Multiplayer sync component (must be inside Canvas + XR) ----

interface MultiplayerSyncProps {
  worldId: string;
  localAvatarRef?: React.RefObject<THREE.Group | null>;
}

const MultiplayerSync: React.FC<MultiplayerSyncProps> = ({ worldId, localAvatarRef }) => {
  const avatarUrl = useWorldStore((s) => s.avatarUrl);
  const userName = useWorldStore((s) => s.userName);
  const user = useWorldStore((s) => s.user);
  const isOwner = useWorldStore((s) => s.isOwner);
  const isPresenting = useXR((s) => !!s.session);
  const origin = useXR((s) => s.origin);
  const { camera } = useThree();

  const [playerList, setPlayerList] = useState<RemotePlayerInfo[]>([]);
  const transformRefs = useRef<Record<string, React.MutableRefObject<RemotePlayerTransform>>>({});
  // Track last heartbeat time per remote player
  const lastHeartbeat = useRef<Record<string, number>>({});
  // Track remote player info for the online users bar
  const remotePlayerInfo = useRef<Record<string, { userName: string; avatarUrl: string; lookTextureUrl?: string | null; userId?: string | null; animUrls?: Record<string, string> | null }>>({});
  // Last time a MOVE packet arrived per player — heartbeat position updates are
  // only applied to idle players (no recent move) so moving players don't snap.
  const lastMovePacket = useRef<Record<string, number>>({});
  // Peers we've already asked for their info via player:whois (avoid spamming).
  const whoisRequested = useRef<Set<string>>(new Set());
  // Live remote-player count, mirrored from rebuildPlayerList so the ~15Hz send
  // loop can adapt its rate without depending on render-closure freshness.
  const remoteCountRef = useRef(0);
  // Last time LOD tiers were recomputed (throttled — no need to re-rank every frame).
  const lodTime = useRef(0);
  // Local player's latest position/rotation (updated every frame, even when
  // stationary) so join/heartbeat packets can carry a real spawn position —
  // otherwise a standing-still player never sends a move and stays invisible.
  const currentPos = useRef<[number, number, number]>([0, 0, 0]);
  const currentRot = useRef(0);
  // False until we have a real position (world + avatar loaded). Until then
  // packets carry no position and peers place us at the world's spawn point,
  // not wherever the default camera happens to sit.
  const hasPos = useRef(false);

  const lastSendTime = useRef(0);
  const lastSentPos = useRef<[number, number, number]>([0, 0, 0]);
  const lastSentRot = useRef(0);
  const lastSentAnim = useRef<'idle' | 'walk' | 'run'>('idle');
  const idleFrames = useRef(0);
  const _worldPos = useRef(new THREE.Vector3());
  const presenceKeyRef = useRef<string>('');
  // Signature of the last published player list. Heartbeats arrive every 3 s
  // from every peer and each one rebuilds the list; publishing an identical
  // list re-rendered every remote avatar and every onlinePlayers subscriber.
  const lastListSig = useRef('');
  // Sends a heartbeat right away (set while the channel is live). Used when our
  // name or avatar changes, so peers don't show a placeholder name for 3 s.
  const sendHeartbeatRef = useRef<(() => void) | null>(null);

  // ---- Expire edit locks whose "done" message never arrived ----
  // A lock is taken when someone starts dragging and refreshed while they
  // drag; if the unlock broadcast is lost, the object would stay frozen for
  // everyone else. 15 s without a sign of the drag releases it.
  useEffect(() => {
    const t = setInterval(() => {
      const st = useWorldStore.getState();
      const now = Date.now();
      for (const [id, lock] of Object.entries(st.editLocks)) {
        if (lock && now - (lock.at ?? 0) > 15000) st.setEditLock(id, null);
      }
    }, 5000);
    return () => clearInterval(t);
  }, []);

  // ---- Release any edit locks held by a departed player ----
  const clearLocksForUser = useCallback((key: string) => {
    const store = useWorldStore.getState();
    for (const [objId, lock] of Object.entries(store.editLocks)) {
      if (lock && lock.userId === key) store.setEditLock(objId, null);
    }
  }, []);

  // ---- Rebuild player list from heartbeat map ----
  const rebuildPlayerList = useCallback(() => {
    const now = Date.now();
    const alive: RemotePlayerInfo[] = [];
    const staleKeys: string[] = [];

    for (const [key, lastTime] of Object.entries(lastHeartbeat.current)) {
      if (now - lastTime > HEARTBEAT_TIMEOUT) {
        staleKeys.push(key);
        continue;
      }
      const info = remotePlayerInfo.current[key];
      if (info) {
        alive.push({ id: key, avatarUrl: info.avatarUrl, lookTextureUrl: info.lookTextureUrl, userName: info.userName, animUrls: info.animUrls });
      }
    }

    // Clean up stale players (and release any locks they held)
    for (const key of staleKeys) {
      delete lastHeartbeat.current[key];
      delete remotePlayerInfo.current[key];
      delete transformRefs.current[key];
      delete lastMovePacket.current[key];
      whoisRequested.current.delete(key);
      clearLocksForUser(key);
    }

    remoteCountRef.current = alive.length;
    const state = useWorldStore.getState();
    const sig = JSON.stringify([alive, presenceKeyRef.current, state.userName, state.avatarUrl, state.user?.id,
      alive.map((p) => remotePlayerInfo.current[p.id]?.userId ?? null)]);
    if (sig === lastListSig.current) return;
    lastListSig.current = sig;
    setPlayerList(alive);
    useWorldStore.getState().setMultiplayerInfo({ playerCount: alive.length, connected: true });

    // Update online users bar (include self)
    const allPlayers: { id: string; userName: string; avatarUrl: string; userId?: string | null }[] = [
      { id: presenceKeyRef.current, userName: state.userName || 'Player', avatarUrl: state.avatarUrl || '', userId: state.user?.id || null },
    ];
    for (const p of alive) {
      const info = remotePlayerInfo.current[p.id];
      allPlayers.push({ id: p.id, userName: p.userName, avatarUrl: p.avatarUrl, userId: info?.userId });
    }
    state.setOnlinePlayers(allPlayers);
  }, []);

  // ---- Supabase Realtime setup ----
  useEffect(() => {
    if (!worldId) return;

    const channel = getChannel(worldId);
    presenceKeyRef.current = getPresenceKey() || crypto.randomUUID();
    console.log('[Multiplayer] Presence key:', presenceKeyRef.current);

    const spawnPoint = (): [number, number, number] => {
      const sp = useWorldStore.getState().spawnPosition;
      return Array.isArray(sp) && sp.length === 3 ? [sp[0], sp[1], sp[2]] : [0, 0, 0];
    };

    // ---- Register a remote player ----
    const registerRemotePlayer = (
      key: string,
      userName: string,
      avatarUrl: string,
      userId?: string | null,
      animUrls?: Record<string, string> | null,
      position?: [number, number, number],
      rotation?: number,
      lookTextureUrl?: string | null,
    ) => {
      lastHeartbeat.current[key] = Date.now();
      const prev = remotePlayerInfo.current[key];
      remotePlayerInfo.current[key] = {
        userName,
        userId: userId || null,
        ...mergeAvatarAppearance(prev, { avatarUrl, lookTextureUrl, animUrls }),
      };
      // Spawn at the sender's real position so a player who joins and stands
      // still is visible immediately (previously started off-screen at y=-100).
      const startPos = Array.isArray(position) ? position : spawnPoint();
      if (!transformRefs.current[key]) {
        transformRefs.current[key] = {
          current: { position: startPos, rotation: rotation || 0, animationState: 'idle' },
        };
      } else if (position) {
        transformRefs.current[key].current.position = startPos;
        if (typeof rotation === 'number') transformRefs.current[key].current.rotation = rotation;
      }
    };

    // ---- Broadcast: player join ----
    channel.on('broadcast', { event: 'player:join' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKeyRef.current) return;
      console.log('[Multiplayer] Player joined:', data.userName, data.key);
      registerRemotePlayer(data.key, data.userName || 'Player', data.avatarUrl || '', data.userId, data.animUrls, data.p, data.r, data.lookTextureUrl);
      rebuildPlayerList();

      // Reply with player:here (NOT player:join) so the new player discovers us without a loop
      const state = useWorldStore.getState();
      channel.send({
        type: 'broadcast',
        event: 'player:here',
        payload: {
          key: presenceKeyRef.current,
          userName: state.userName || 'Player',
          avatarUrl: state.avatarUrl || '',
          lookTextureUrl: state.avatarLookTextureUrl,
          userId: state.user?.id || null,
          animUrls: readLocalAnimUrls(),
          p: hasPos.current ? currentPos.current : undefined,
          r: hasPos.current ? currentRot.current : undefined,
        },
      });
    });

    // ---- Broadcast: player:here (reply to join — no further reply) ----
    channel.on('broadcast', { event: 'player:here' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKeyRef.current) return;
      console.log('[Multiplayer] Player already here:', data.userName, data.key);
      registerRemotePlayer(data.key, data.userName || 'Player', data.avatarUrl || '', data.userId, data.animUrls, data.p, data.r, data.lookTextureUrl);
      rebuildPlayerList();
    });

    // ---- Broadcast: whois request ----
    // Recovers avatar/anim info for the rare case where a peer was auto-
    // registered from a bare move packet (we missed their join, so we have no
    // animUrls). The addressed player replies with a full player:here.
    channel.on('broadcast', { event: 'player:whois' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.target || data.target !== presenceKeyRef.current) return;
      const state = useWorldStore.getState();
      channel.send({
        type: 'broadcast',
        event: 'player:here',
        payload: {
          key: presenceKeyRef.current,
          userName: state.userName || 'Player',
          avatarUrl: state.avatarUrl || '',
          lookTextureUrl: state.avatarLookTextureUrl,
          userId: state.user?.id || null,
          animUrls: readLocalAnimUrls(),
          p: hasPos.current ? currentPos.current : undefined,
          r: hasPos.current ? currentRot.current : undefined,
        },
      });
    });

    // ---- Broadcast: evict my other sessions ----
    // Every tab joins the room as its own participant, so opening a world
    // twice puts two of you on the floor. This lets one tab dismiss the
    // others: it names the session to keep, and any tab belonging to the same
    // account that is NOT that one shows itself out. Scoped to the sender's
    // own user id — it can never remove another person.
    channel.on('broadcast', { event: 'session:evict' }, (payload: any) => {
      const data = payload.payload;
      const me = useWorldStore.getState().user?.id || null;
      if (!data?.userId || !me || data.userId !== me) return;
      if (data.keep === presenceKeyRef.current) return;
      console.log('[Multiplayer] Evicted by another tab of this account');
      channel.send({ type: 'broadcast', event: 'player:leave', payload: { key: presenceKeyRef.current } });
      try { disconnectChannel(); } catch { /* already gone */ }
      window.location.replace('/dashboard?closed=duplicate');
    });

    // Exposed for the viewer's "close my other tabs" control. Kept on window
    // rather than threaded through props because MultiplayerSync is mounted
    // far from the HUD that needs it.
    (window as any).__gxEvictOtherSessions = () => {
      const me = useWorldStore.getState().user?.id || null;
      if (!me) return 0;
      channel.send({
        type: 'broadcast',
        event: 'session:evict',
        payload: { userId: me, keep: presenceKeyRef.current },
      });
      return 1;
    };

    // ---- Broadcast: player leave ----
    channel.on('broadcast', { event: 'player:leave' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key) return;
      console.log('[Multiplayer] Player left:', data.key);
      delete lastHeartbeat.current[data.key];
      delete remotePlayerInfo.current[data.key];
      delete transformRefs.current[data.key];
      delete lastMovePacket.current[data.key];
      whoisRequested.current.delete(data.key);
      clearLocksForUser(data.key);
      rebuildPlayerList();
    });

    // ---- Broadcast: heartbeat (keeps player alive) ----
    channel.on('broadcast', { event: 'player:heartbeat' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKeyRef.current) return;
      lastHeartbeat.current[data.key] = Date.now();

      // Update info if changed (never drop known anim URLs — heartbeat may omit them)
      if (data.userName || data.avatarUrl || data.animUrls || 'lookTextureUrl' in data) {
        const prev = remotePlayerInfo.current[data.key];
        remotePlayerInfo.current[data.key] = {
          userName: data.userName || prev?.userName || 'Player',
          userId: data.userId ?? prev?.userId,
          ...mergeAvatarAppearance(prev, data),
        };
      }

      // Keep idle (non-moving) players positioned: apply the heartbeat's
      // position only when no recent move packet has arrived, so we never
      // rubber-band someone who's actively walking.
      if (Array.isArray(data.p) && transformRefs.current[data.key]) {
        const sinceMove = Date.now() - (lastMovePacket.current[data.key] || 0);
        if (sinceMove > 1200) {
          transformRefs.current[data.key].current.position = data.p;
          if (typeof data.r === 'number') transformRefs.current[data.key].current.rotation = data.r;
          transformRefs.current[data.key].current.animationState = 'idle';
        }
      }

      // Ensure transform ref (in case we missed the join)
      if (!transformRefs.current[data.key]) {
        transformRefs.current[data.key] = {
          current: {
            position: Array.isArray(data.p) ? data.p : spawnPoint(),
            rotation: typeof data.r === 'number' ? data.r : 0,
            animationState: 'idle',
          },
        };
      }

      // Apply appearance changes and recover peers whose join was missed.
      rebuildPlayerList();
    });

    // ---- Broadcast: movement packets ----
    channel.on('broadcast', { event: 'player:move' }, (payload: any) => {
      const move = decodeMove(payload.payload);
      if (!move) return;

      // Update transform
      const ref = transformRefs.current[move.key];
      if (ref?.current.tracking && move.tracking && move.tracking.time <= ref.current.tracking.time) return;
      if (ref) {
        ref.current.position = move.position;
        ref.current.rotation = move.rotation;
        ref.current.animationState = move.animationState;
        ref.current.grounded = move.grounded;
        ref.current.tracking = move.tracking;
        ref.current.trackingReceivedAt = performance.now();
      }

      // Movement counts as heartbeat
      if (move.key !== presenceKeyRef.current) {
        lastHeartbeat.current[move.key] = Date.now();
        lastMovePacket.current[move.key] = Date.now();

        // Auto-register if we missed their join
        if (!transformRefs.current[move.key]) {
          transformRefs.current[move.key] = {
            current: { position: move.position, rotation: move.rotation, animationState: move.animationState, grounded: move.grounded, tracking: move.tracking, trackingReceivedAt: performance.now() },
          };
          if (!remotePlayerInfo.current[move.key]) {
            remotePlayerInfo.current[move.key] = { userName: 'Player', avatarUrl: '', userId: null, animUrls: null };
          }
          rebuildPlayerList();
        }

        // We only have a bare move for this peer (missed their join) and no
        // avatar/anim info — ask them once so their avatar + clips resolve.
        if (!remotePlayerInfo.current[move.key]?.animUrls && !whoisRequested.current.has(move.key)) {
          whoisRequested.current.add(move.key);
          try {
            getChannel(worldId).send({
              type: 'broadcast',
              event: 'player:whois',
              payload: { key: presenceKeyRef.current, target: move.key },
            });
          } catch { /* channel not ready */ }
        }
      }
    });

    // ---- Broadcast: someone is presenting their screen ----
    channel.on('broadcast', { event: 'screen:live' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKeyRef.current) return;
      onRemoteScreenLive(data.live === true);
    });

    // ---- Broadcast: emotes ----
    channel.on('broadcast', { event: 'player:emote' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKeyRef.current || (data.name !== null && typeof data.name !== 'string')) return;

      // Ensure a transform ref exists (in case we missed their join)
      if (!transformRefs.current[data.key]) {
        transformRefs.current[data.key] = {
          current: { position: [0, -100, 0], rotation: 0, animationState: 'idle' },
        };
        if (!remotePlayerInfo.current[data.key]) {
          remotePlayerInfo.current[data.key] = { userName: 'Player', avatarUrl: '', userId: null };
        }
        rebuildPlayerList();
      }

      transformRefs.current[data.key].current.emote = data.name === null ? null : { name: data.name, nonce: data.nonce || Date.now() };
      lastHeartbeat.current[data.key] = Date.now();
    });

    // ---- Broadcast: world object sync ----
    channel.on('broadcast', { event: 'world:object:added' }, (payload: any) => {
      const data = toLiteUrls(payload.payload); // lite rendition: swap in this world's optimized files
      const store = useWorldStore.getState();
      withRemoteOrigin(() => {
        if (data.objectType === 'content' && data.id && data.item) {
          store.addContentItem(data.id, data.item);
        } else if (data.objectType === 'primitive' && data.id && data.item) {
          store.addPrimitive(data.id, data.item);
        } else if (data.objectType === 'light' && data.id && data.item) {
          store.addLight(data.id, data.item);
        } else if (data.objectType === 'environment' && data.id && data.item) {
          store.addEnvironmentModel(data.id, data.item);
        } else if (data.objectType === 'skybox' && data.id && data.item) {
          store.addSkybox(data.id, data.item);
        } else if (data.objectType === 'screenprop' && data.id && data.item) {
          store.addScreenProp(data.id, data.item);
        }
      });
      if (data.id) store.addRemoteObjectId(data.id);
    });

    channel.on('broadcast', { event: 'world:object:updated' }, (payload: any) => {
      const data = toLiteUrls(payload.payload);
      const store = useWorldStore.getState();
      withRemoteOrigin(() => {
        if (data.objectType === 'content' && data.id && data.updates) {
          store.updateContentItem(data.id, data.updates);
        } else if (data.objectType === 'primitive' && data.id && data.updates) {
          store.updatePrimitive(data.id, data.updates);
        } else if (data.objectType === 'light' && data.id && data.updates) {
          store.updateLight(data.id, data.updates);
        } else if (data.objectType === 'environment' && data.id && data.updates) {
          store.updateEnvironmentModel(data.id, data.updates);
        } else if (data.objectType === 'skybox' && data.id && data.updates) {
          store.addSkybox(data.id, data.updates); // full-entry replace (no partial-update action)
        } else if (data.objectType === 'screenprop' && data.id && data.updates) {
          store.updateScreenProp(data.id, data.updates);
        }
      });
    });

    channel.on('broadcast', { event: 'world:object:deleted' }, (payload: any) => {
      const data = payload.payload;
      const store = useWorldStore.getState();
      withRemoteOrigin(() => {
        if (data.objectType === 'content' && data.id) {
          store.deleteContentItem(data.id);
        } else if (data.objectType === 'primitive' && data.id) {
          store.deletePrimitive(data.id);
        } else if (data.objectType === 'light' && data.id) {
          store.deleteLight(data.id);
        } else if (data.objectType === 'environment' && data.id) {
          store.deleteEnvironmentModel(data.id);
        } else if (data.objectType === 'skybox' && data.id) {
          store.deleteSkybox(data.id);
        } else if (data.objectType === 'screenprop' && data.id) {
          store.deleteScreenProp(data.id);
        }
      });
    });

    // ---- Broadcast: shared world settings (ground, atmosphere, skybox selection, spawn) ----
    channel.on('broadcast', { event: 'world:settings:updated' }, (payload: any) => {
      const data = payload.payload;
      if (!data) return;
      const store = useWorldStore.getState();
      withRemoteOrigin(() => {
        if (data.ground) store.setGroundSettings(data.ground);
        if (typeof data.groundVisible === 'boolean') store.setGroundVisible(data.groundVisible);
        if (data.atmosphere) store.setAtmosphere(data.atmosphere);
        if ('currentSkyboxId' in data) store.setCurrentSkyboxId(data.currentSkyboxId);
        if (data.spawnPosition) store.setSpawnPosition(data.spawnPosition);
      });
    });

    // ---- Broadcast: edit locks ----
    channel.on('broadcast', { event: 'world:object:lock' }, (payload: any) => {
      const data = payload.payload;
      if (data?.id && data?.userId) {
        useWorldStore.getState().setEditLock(data.id, { userId: data.userId, userName: data.userName || 'Player' });
      }
    });

    channel.on('broadcast', { event: 'world:object:unlock' }, (payload: any) => {
      const data = payload.payload;
      if (data?.id) {
        useWorldStore.getState().setEditLock(data.id, null);
      }
    });

    // ---- Broadcast: live transform (during drag, before final update) ----
    channel.on('broadcast', { event: 'world:object:transform' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.id) return;
      const store = useWorldStore.getState();
      // Still dragging: keep their edit lock fresh (stale ones expire below).
      const held = store.editLocks[data.id];
      if (held) store.setEditLock(data.id, held);
      const updates: any = {};
      if (data.position) updates.position = data.position;
      if (data.rotation) updates.rotation = data.rotation;
      if (data.scale) updates.scale = data.scale;
      // Apply to whichever collection holds this object
      withRemoteOrigin(() => {
        if (store.primitives[data.id]) store.updatePrimitive(data.id, updates);
        else if (store.lights[data.id]) store.updateLight(data.id, updates);
        else if (store.contentItems[data.id]) {
          // ContentItem.scale is a scalar — mirror the final-update mapping
          if (Array.isArray(updates.scale)) updates.scale = updates.scale[0];
          store.updateContentItem(data.id, updates);
        }
      });
    });

    // ---- Broadcast: knock requests (world locking) ----
    channel.on('broadcast', { event: 'knock:request' }, (payload: any) => {
      const data = payload.payload;
      const store = useWorldStore.getState();
      if (store.isOwner && data?.userId && data?.userName) {
        store.addKnockRequest({ userId: data.userId, userName: data.userName, timestamp: data.timestamp || Date.now() });
      }
    });

    // ---- Broadcast: role updates (builder/host grant/revoke) ----
    channel.on('broadcast', { event: 'role:update' }, (payload: any) => {
      const data = payload.payload;
      if (data?.targetUserId && data?.role) {
        const store = useWorldStore.getState();
        if (store.user?.id === data.targetUserId) {
          store.setRole(data.role);
        }
      }
    });

    // ---- Broadcast: world lock (creator locks everyone out) ----
    channel.on('broadcast', { event: 'world:lock' }, (payload: any) => {
      const data = payload.payload;
      const store = useWorldStore.getState();
      if (data?.locked && !store.isOwner) {
        store.setWorldLocked(true);
        alert('The world creator has locked this world. You will be redirected.');
        window.location.href = store.user ? '/dashboard' : '/';
      }
    });

    // ---- Dashboard "online now" ----
    // useWorldPresence watches presence on `dashboard-presence:<world>`; nothing
    // ever tracked into it, so world cards always read "Empty". Track ourselves
    // there once the world channel is live.
    let dashboardPresence: ReturnType<typeof supabase.channel> | null = null;
    const trackDashboardPresence = () => {
      if (dashboardPresence) return;
      const state = useWorldStore.getState();
      dashboardPresence = supabase.channel(`dashboard-presence:${worldId}`, {
        config: { presence: { key: presenceKeyRef.current } },
      });
      dashboardPresence.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          dashboardPresence?.track({
            userName: state.userName || 'Player',
            avatarUrl: state.avatarUrl || '',
          lookTextureUrl: state.avatarLookTextureUrl,
          });
        }
      });
    };

    // ---- Subscribe ----
    const stopStatus = onChannelSubscribed(worldId, (status, err) => {
      console.log('[Multiplayer] Channel status:', status, err || '');
      if (status === 'SUBSCRIBED') {
        console.log('[Multiplayer] Supabase channel subscribed for world:', worldId);
        useWorldStore.getState().setMultiplayerInfo({ connected: true });

        // Set role based on ownership
        const state = useWorldStore.getState();
        if (state.isOwner) {
          state.setRole('owner');
        }

        // Announce join via broadcast
        channel.send({
          type: 'broadcast',
          event: 'player:join',
          payload: {
            key: presenceKeyRef.current,
            userName: state.userName || 'Player',
            avatarUrl: state.avatarUrl || '',
          lookTextureUrl: state.avatarLookTextureUrl,
            userId: state.user?.id || null,
            animUrls: readLocalAnimUrls(),
            p: hasPos.current ? currentPos.current : undefined,
            r: hasPos.current ? currentRot.current : undefined,
          },
        });
        console.log('[Multiplayer] Join announced via broadcast');
        trackDashboardPresence();
      }
    });

    // ---- Heartbeat: broadcast every 3s so others know we're alive ----
    const sendHeartbeat = () => {
      try {
        const state = useWorldStore.getState();
        channel.send({
          type: 'broadcast',
          event: 'player:heartbeat',
          payload: {
            // Include appearance for late joins and mid-session wardrobe changes.
            animUrls: readLocalAnimUrls(),
            key: presenceKeyRef.current,
            userName: state.userName || 'Player',
            avatarUrl: state.avatarUrl || '',
          lookTextureUrl: state.avatarLookTextureUrl,
            userId: state.user?.id || null,
            p: hasPos.current ? currentPos.current : undefined,
            r: hasPos.current ? currentRot.current : undefined,
          },
        });
      } catch (e) { /* channel not ready */ }
    };
    const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    sendHeartbeatRef.current = sendHeartbeat;

    // ---- Drop stale players (crashed / closed without a goodbye) ----
    // Checked every second (cheap now that unchanged lists aren't re-published),
    // so a vanished player is removed HEARTBEAT_TIMEOUT..+1 s after their last packet.
    const cleanupInterval = setInterval(() => {
      rebuildPlayerList();
    }, 1000);

    // Leaving by loading another page (a portal to another world, a refresh,
    // closing the tab) never unmounts React, so say goodbye here too: otherwise
    // this session lingers in the room and shows up as "open in another tab".
    let left = false;
    const leaveNow = (e?: Event) => {
      if (left) return;
      left = true;
      try { channel.send({ type: 'broadcast', event: 'player:leave', payload: { key: presenceKeyRef.current } }); } catch { /* gone */ }
      // Closing a tab tears the websocket down before that frame is flushed,
      // so peers only noticed via the 8 s heartbeat timeout. Also post the
      // goodbye to Realtime's REST broadcast endpoint with keepalive, which
      // the browser completes after the page is gone.
      if (e?.type === 'pagehide') {
        try {
          const ch = channel as any;
          const sock = ch.socket;
          if (ch.broadcastEndpointURL && sock?.apiKey) {
            fetch(ch.broadcastEndpointURL, {
              method: 'POST',
              keepalive: true,
              headers: {
                apikey: sock.apiKey,
                Authorization: `Bearer ${sock.accessTokenValue || sock.apiKey}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ messages: [{ topic: ch.subTopic, event: 'player:leave', payload: { key: presenceKeyRef.current }, private: !!ch.private }] }),
            }).catch(() => { /* best effort */ });
          }
        } catch { /* best effort */ }
      }
      try { dashboardPresence?.untrack(); } catch { /* gone */ }
    };
    window.addEventListener('pagehide', leaveNow);
    window.addEventListener('gathrxr:leave-world', leaveNow);

    return () => {
      window.removeEventListener('pagehide', leaveNow);
      window.removeEventListener('gathrxr:leave-world', leaveNow);
      // Announce leave
      if (!left) {
        try {
          channel.send({
            type: 'broadcast',
            event: 'player:leave',
            payload: { key: presenceKeyRef.current },
          });
        } catch (e) { /* already disconnected */ }
      }

      stopStatus();
      sendHeartbeatRef.current = null;
      lastListSig.current = '';
      clearInterval(heartbeatInterval);
      clearInterval(cleanupInterval);
      if (dashboardPresence) supabase.removeChannel(dashboardPresence);
      disconnectChannel();
      transformRefs.current = {};
      lastHeartbeat.current = {};
      remotePlayerInfo.current = {};
      setPlayerList([]);
      const store = useWorldStore.getState();
      store.setMultiplayerInfo({ connected: false, playerCount: 0 });
      store.setOnlinePlayers([]);
    };
  }, [worldId, rebuildPlayerList]);

  // Our profile name and wardrobe avatar load after the join announcement
  // (which goes out with a generated name / no avatar). Tell peers as soon as
  // they change instead of waiting for the next 3 s heartbeat.
  const avatarLook = useWorldStore((s) => s.avatarLookTextureUrl);
  useEffect(() => {
    sendHeartbeatRef.current?.();
  }, [userName, avatarUrl, avatarLook]);

  const hadTracking = useRef(false);
  const lastSentGrounded = useRef(true);

  // ---- Send local position, rate adapts to room size ----
  // Fewer players → smoother 15Hz; crowded rooms → ~8Hz to roughly halve the
  // O(N²) broadcast volume. The visual difference is negligible with
  // interpolation on the receiving side.
  useFrame((state, _delta, frame) => {
    const now = performance.now();

    // ---- Assign avatar LOD tiers by camera distance + rank (throttled) ----
    if (now - lodTime.current > LOD_INTERVAL_MS) {
      lodTime.current = now;
      const keys = Object.keys(transformRefs.current);
      if (keys.length > 0) {
        const ranked = keys.map((key) => {
          const p = transformRefs.current[key].current.position;
          _lodVec.set(p[0], p[1], p[2]);
          return { key, dist: camera.position.distanceTo(_lodVec) };
        }).sort((a, b) => a.dist - b.dist);
        ranked.forEach((e, i) => {
          const ref = transformRefs.current[e.key];
          if (!ref) return;
          let lod: 'full' | 'frozen' | 'billboard';
          if (e.dist > LOD_BILLBOARD_DIST) lod = 'billboard';
          else if (i < LOD_MAX_FULL && e.dist < LOD_FULL_DIST) lod = 'full';
          else lod = 'frozen';
          ref.current.lod = lod;
        });
      }
    }

    const totalPlayers = remoteCountRef.current + 1;
    const sendInterval = isPresenting || totalPlayers <= 5 ? 66 : totalPlayers <= 10 ? 100 : 125; // ~15 / ~10 / ~8 Hz
    if (now - lastSendTime.current < sendInterval) return;

    let pos: [number, number, number] = [0, 0, 0];
    let rot = 0;

    let tracking: AvatarTracking | null = null;
    if (isPresenting && origin) {
      origin.updateWorldMatrix(true, false);
      const space = state.gl.xr.getReferenceSpace();
      const viewer = frame && space ? frame.getViewerPose(space) : null;
      origin.getWorldPosition(_worldPos.current);
      pos = _worldPos.current.toArray();
      rot = new THREE.Euler().setFromQuaternion(origin.getWorldQuaternion(new THREE.Quaternion()), 'YXZ').y;
      if (viewer && frame && space) {
        const headWorld = new THREE.Matrix4().fromArray(viewer.transform.matrix).premultiply(origin.matrixWorld);
        const headPosition = new THREE.Vector3().setFromMatrixPosition(headWorld);
        const headOrientation = new THREE.Quaternion().setFromRotationMatrix(headWorld);
        rot = new THREE.Euler().setFromQuaternion(headOrientation, 'YXZ').y + Math.PI;
        // Seated mode lifts the rig; others should see feet on the floor.
        pos = [headPosition.x, _worldPos.current.y - vrLift.applied, headPosition.z];
        const avatarWorld = new THREE.Matrix4().compose(new THREE.Vector3(...pos), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rot), new THREE.Vector3(1, 1, 1));
        tracking = { v: 1, time: performance.timeOrigin + now, referenceHeight: sampleAvatarHeight(state.gl.xr.getSession()!, viewer.transform.position.y + vrLift.applied, now), head: relativeTrackingPose(viewer.transform, origin.matrixWorld, avatarWorld), left: null, right: null };
        for (const source of state.gl.xr.getSession()?.inputSources || []) {
          if (source.handedness !== 'left' && source.handedness !== 'right') continue;
          const grip = source.gripSpace || source.hand?.get('wrist');
          const pose = grip ? frame.getPose(grip, space) : null;
          if (pose) tracking[source.handedness] = relativeTrackingPose(pose.transform, origin.matrixWorld, avatarWorld);
        }
      }
    } else if (localAvatarRef?.current) {
      const g = localAvatarRef.current;
      if ((g as unknown as THREE.Camera).isCamera) {
        // View-only visitor: the first-person camera stands in for the body.
        // Peers expect feet + avatar facing (+Z), the camera is eyes looking -Z.
        pos = [g.position.x, g.position.y - VIEW_ONLY_EYE_HEIGHT, g.position.z];
        rot = g.rotation.y + Math.PI;
      } else {
        pos = [g.position.x, g.position.y, g.position.z];
        rot = g.rotation.y;
      }
    } else {
      return;
    }

    // Record our live position every frame (even when standing still) so
    // join/heartbeat packets carry a real spawn point for remote peers.
    currentPos.current = pos;
    currentRot.current = rot;
    hasPos.current = true;

    const dx = pos[0] - lastSentPos.current[0];
    const dz = pos[2] - lastSentPos.current[2];
    const dist = Math.sqrt(dx * dx + dz * dz);
    const elapsed = (now - lastSendTime.current) / 1000; // actual seconds since last send
    const speed = elapsed > 0 ? dist / elapsed : 0;
    const dr = Math.abs(rot - lastSentRot.current);

    let rawAnim: 'idle' | 'walk' | 'run' = 'idle';
    if (speed > 9) rawAnim = 'run';
    else if (speed > 1.5) rawAnim = 'walk';

    let animState = lastSentAnim.current;
    if (rawAnim !== 'idle') {
      animState = rawAnim;
      idleFrames.current = 0;
    } else {
      idleFrames.current++;
      if (idleFrames.current > 5) animState = 'idle';
    }

    const grounded = isPresenting ? true : localAvatarRef?.current?.userData.avatarGrounded !== false;
    if (grounded === lastSentGrounded.current && !tracking && !hadTracking.current && Math.abs(pos[1] - lastSentPos.current[1]) < .005 && dist < 0.03 && dr < 0.02 && animState === lastSentAnim.current) return;

    const channel = getChannel(worldId);
    channel.send({
      type: 'broadcast',
      event: 'player:move',
      payload: { ...encodeMove(presenceKeyRef.current, pos, rot, animState), tracking, grounded },
    });

    lastSentGrounded.current = grounded;
    hadTracking.current = !!tracking;
    lastSentPos.current = pos;
    lastSentRot.current = rot;
    lastSentAnim.current = animState;
    lastSendTime.current = now;
  });

  return (
    <>
      {playerList.map((p) => {
        const tRef = transformRefs.current[p.id];
        if (!tRef) return null;
        return (
          <PlayerSafetyGate key={p.id} playerKey={p.id} userId={remotePlayerInfo.current[p.id]?.userId} userName={p.userName} transformRef={tRef}>
            <RemoteAvatar info={p} transformRef={tRef} userId={remotePlayerInfo.current[p.id]?.userId} />
          </PlayerSafetyGate>
        );
      })}
      <SafetyWorldHost worldId={worldId} />
    </>
  );
};

export default MultiplayerSync;
